// Thin JSON client for /api/v1. The session travels in an httpOnly cookie, so
// there is no token handling here at all.

export interface FieldIssue {
  path: string;
  message: string;
}

/** A failed API call. `message` is user-facing (Spanish), written by the API. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: FieldIssue[] = [],
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  // Files travel as multipart/form-data: the browser sets that content type and its boundary.
  const isForm = body instanceof FormData;
  try {
    response = await fetch(`/api/v1${path}`, {
      method,
      credentials: "same-origin",
      headers: body === undefined || isForm ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", "No hay conexión con el servidor. Revisa tu internet.");
  }
  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data as { error?: { code?: string; message?: string; issues?: FieldIssue[] } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "http_error",
      error?.message ?? "Ocurrió un error inesperado. Intenta de nuevo.",
      error?.issues ?? [],
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  postForm: <T>(path: string, form: FormData) => request<T>("POST", path, form),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: (path: string) => request<void>("DELETE", path),
};

/** First validation message for a form field (`path` as returned by the API). */
export const issueFor = (error: unknown, path: string) =>
  error instanceof ApiError ? error.issues.find((i) => i.path === path)?.message : undefined;

export const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Ocurrió un error inesperado.";
